import { abs } from "@suse-o11y/abs";
import type { Time } from "@suse-o11y/date-time/time";
import { max } from "@suse-o11y/max";
import { type Monitor, Signal, batch } from "@suse-o11y/signal";

import type { TimelineBrushAreaState } from "./TimelineBrushArea";

const IS_BRUSHING_THRESHOLD_PX = 2;

export class TimelineControlsState {
	private readonly brushStart = Signal<{ time: Time; px?: number }>({ time: -1 });
	private readonly hovering = Signal(false);
	private readonly brushing = Signal(false);
	private readonly preventsClick = Signal(false);

	public readonly x: Signal<number>;

	private readonly _shift = Signal(-1);

	public constructor() {
		const x = Signal(-1);

		this.x = Signal(
			(monitor) => max(0, x.read(monitor) - this._shift.read(monitor)),
			(next) => {
				x.write(next);

				this.brushing.write((brushing, monitor) => {
					// already brushing - keep going
					if (brushing) {
						return true;
					}

					const { px: brushingStartPx } = this.brushStart.read(monitor);

					// not brushing yet
					if (brushingStartPx == null) {
						return false;
					}

					// start brushing only when a threshold is reached, otherwise it might be too annoying for a user
					return abs(next - brushingStartPx) > IS_BRUSHING_THRESHOLD_PX;
				});
			},
		);
	}

	public toBrushArea(monitor: Monitor): TimelineBrushAreaState {
		return {
			start: this.brushStart.read(monitor).time,
			end: this.x,
		};
	}

	public isBrushing(monitor: Monitor): boolean {
		return this.brushing.read(monitor);
	}

	public isHovering(monitor: Monitor): boolean {
		return this.hovering.read(monitor);
	}

	public shouldPreventClick(monitor: Monitor): boolean {
		return this.preventsClick.read(monitor);
	}

	public enterTimeline(x: number, containerLeft: number): void {
		batch(() => {
			this.hovering.write(true);
			this._shift.write(containerLeft);
			this.x.write(x);
		});
	}

	public leaveTimeline(): void {
		this.hovering.write(false);
	}

	public startBrushing(time: Time, px: number): void {
		batch(() => {
			this.preventsClick.write(false);
			this.brushStart.write({ time, px });
		});
	}

	public endBrushing(): void {
		batch(() => {
			this.brushing.write(false);
			this.brushStart.write({ time: -1 });
		});
	}

	public cancelBrushing(): void {
		batch(() => {
			this.preventsClick.write(true);
			this.endBrushing();
		});
	}
}
